// app/src/lib/cashflow/expenseKeywords.js — keyword categorizer for operating
// expenses, configured in Account → Utilities / Maintenance keywords.
//
// Same user-driven model as cashflow/rent + taxes: the operator manages a
// keyword list per category in settings, and Kanjo tags any matching Plaid /
// uploaded transaction. Unlike rent/taxes, these ship with a small set of
// sensible DEFAULTS (a brand-new profile starts with them, and can edit), so
// common bills auto-categorize on day one.
//
//   utility:     electricity, natural gas, internet/phone. NOT water, sewer, or
//                garbage — for most restaurant tenants the landlord pays those,
//                so they're left out of the defaults on purpose.
//   maintenance: fire inspection, grease-trap + hood cleaning, dish-machine
//                rental/maintenance, and the trades.
//
// Storage: userProfile.expenseKeywords = { utility: [...], maintenance: [...] }.

import { db } from '../db.js';
import { currentClaims } from '../auth/cognitoClient.js';

const MAX_KEYWORDS = 60;
export const CATEGORIES = ['utility', 'maintenance'];

// Default seeds — the operator's listed concepts plus the most common vendor
// names / generic terms. Kept short enough to scan + edit in settings.
export const EXPENSE_CATEGORY_KEYWORDS = Object.freeze({
  utility: [
    // 'puget sound' (not '… energy'): bank descriptors truncate ("PUGET SOUND ENER").
    'electricity', 'electric', 'natural gas', 'nw natural', 'puget sound',
    'city light', 'avista', 'internet', 'broadband', 'fiber', 'comcast',
    'xfinity', 'centurylink', 'ziply',
  ],
  maintenance: [
    'fire inspection', 'fire protection', 'fire safety', 'grease trap',
    'hood cleaning', 'kitchen exhaust', 'dishwasher', 'dish machine', 'ecolab',
    'auto-chlor', 'refrigeration', 'hvac', 'plumbing', 'pest control',
    'repair', 'maintenance',
  ],
});

function defaults() {
  return { utility: [...EXPENSE_CATEGORY_KEYWORDS.utility], maintenance: [...EXPENSE_CATEGORY_KEYWORDS.maintenance] };
}

// ── settings storage ─────────────────────────────────────────────────────
// Returns { utility:[], maintenance:[] }. Falls back to the defaults for any
// category the operator hasn't customized yet.
export async function getKeywords() {
  const c = await currentClaims();
  if (!c) return defaults();
  const p = await db.userProfile.get(c.sub);
  const saved = (p && p.expenseKeywords) || {};
  const d = defaults();
  return {
    utility: Array.isArray(saved.utility) ? saved.utility : d.utility,
    maintenance: Array.isArray(saved.maintenance) ? saved.maintenance : d.maintenance,
  };
}

async function setCategory(category, list) {
  const c = await currentClaims();
  if (!c) throw new Error('expenseKeywords: not signed in');
  const clean = [...new Set((list || []).map((s) => String(s).trim()).filter(Boolean))].slice(0, MAX_KEYWORDS);
  const existing = (await db.userProfile.get(c.sub)) || { userId: c.sub };
  const cur = existing.expenseKeywords || {};
  await db.userProfile.put({ ...existing, userId: c.sub, expenseKeywords: { ...cur, [category]: clean } });
  return clean;
}

export const getUtilityKeywords = async () => (await getKeywords()).utility;
export const getMaintenanceKeywords = async () => (await getKeywords()).maintenance;
export const setUtilityKeywords = (list) => setCategory('utility', list);
export const setMaintenanceKeywords = (list) => setCategory('maintenance', list);

// ── matching ───────────────────────────────────────────────────────────────
const SPECIAL = /[^\w ]/;
function escapeRegex(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
function matchesKeyword(hay, k) {
  const key = String(k || '').toLowerCase().trim();
  if (!key) return false;
  // Keywords with non-word chars (e.g. "auto-chlor") use substring — the
  // unusual character carries the specificity. Others use a leading word
  // boundary so short tokens don't hit arbitrary substrings.
  if (SPECIAL.test(key)) return hay.includes(key);
  return new RegExp('\\b' + escapeRegex(key)).test(hay);
}

/**
 * Categorize an expense by merchant name + description against a keyword map
 * (defaults to the built-in seeds; pass the operator's getKeywords() result to
 * honor their settings). Returns 'utility' | 'maintenance' | null. maintenance
 * is checked first so e.g. "GAS APPLIANCE REPAIR" reads as maintenance.
 */
export function categorizeExpense(merchantName, description = '', keywords = EXPENSE_CATEGORY_KEYWORDS) {
  const hay = `${merchantName || ''} ${description || ''}`.toLowerCase();
  if (!hay.trim()) return null;
  for (const bucket of ['maintenance', 'utility']) {
    if ((keywords[bucket] || []).some((k) => matchesKeyword(hay, k))) return bucket;
  }
  return null;
}
